import {
  Component,
  OnInit,
  ViewChildren,
  QueryList,
  ElementRef,
  Input,
  OnChanges,
  SimpleChanges,
} from "@angular/core";
import { MessageService } from "primeng/api";
import { TranslateService } from "@ngx-translate/core";
import { AgencyService } from "../../../../../../../_services";

/**
 * JS-1669 - Working time settings matrix.
 *
 * Rows are the active business rules (served by the backend), columns are the
 * agency's project types plus a leading "Default" column. Each cell shows the
 * effective value: the Default column holds the agency baseline, and a project
 * type either inherits that Default (muted) or carries its own override
 * (highlighted, with a revert-to-default control). A rule is enforced purely
 * by value presence (override -> Default -> not enforced); rules with no value
 * anywhere are shown muted. New rules require no frontend change - the matrix
 * renders whatever the API returns.
 */

interface ProjectType {
  id: number;
  name: string;
}

interface BusinessRule {
  id: number;
  business_rule_id: number;
  key: string;
  name: string;
  unit: string;
  value_type: string; // 'number' | 'compound'
  sub_labels: string[] | null;
  description: string | null;
  sort_order: number;
  enabled: boolean;
  default_value: any; // number | [peak, rest] | null — the CLIENT-level default, null when
                     // the client hasn't set its own.
  // JS-1920 follow-up (2026-11-02): the AGENCY-level default (always from business_rules
  // table). Rendered as a grey placeholder with a "<agency> default" tooltip when the
  // client has no own value and no override, instead of the previous empty-dash.
  agency_default_value?: any;
  overrides: { [projectTypeId: string]: any };
}

interface Column {
  id: number | null; // null = Default column
  name: string;
  isDefault: boolean;
}

interface EditState {
  ruleId: number;
  typeId: number | null; // null = Default column
  compound: boolean;
}

@Component({
  selector: "app-working-time-settings",
  templateUrl: "./working-time-settings.component.html",
  styleUrls: ["./working-time-settings.component.scss"],
})
export class WorkingTimeSettingsComponent implements OnInit, OnChanges {
  // JS-1936: when provided, the matrix is scoped to this client (columns = client's own
  // project types, read/write hit the client endpoints). Null = agency scope (default).
  @Input() clientId: number | null = null;

  rules: BusinessRule[] = [];
  projectTypes: ProjectType[] = [];
  // JS-1920 follow-up (2026-11-02): shown in the "<agency> default" tooltip for grey
  // placeholder cells. Pulled from the client-scope response; falls back to empty so the
  // tooltip simply reads "default" if the backend doesn't ship it (agency-scope).
  agencyName = "";

  loading = false;
  saving = false;

  edit: EditState | null = null;
  editVal = "";
  editVal2 = "";

  hoverRule: number | null = null;
  hoverType: number | null = null;

  // The inline <input> for the cell currently being edited. Rendered via *ngIf,
  // so we focus it programmatically once it exists (HTML `autofocus` does not
  // fire for elements added after the initial render).
  @ViewChildren("editInput") editInputs!: QueryList<ElementRef<HTMLInputElement>>;

  constructor(
    private agencyService: AgencyService,
    private messageService: MessageService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.load();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // JS-1936: on the client-settings panel the clientId arrives async after the client
    // payload fetch resolves. Re-fire load the first time it transitions to a truthy value
    // so the matrix doesn't stay stuck on the empty agency payload loaded in ngOnInit.
    if (changes.clientId && this.clientId && !changes.clientId.previousValue) {
      this.load();
    }
  }

  load(): void {
    this.loading = true;
    const request$ = this.clientId
      ? this.agencyService.getClientBusinessRules(this.clientId)
      : this.agencyService.getBusinessRules();
    request$.subscribe(
      (res) => {
        this.projectTypes = (res && res.project_types) || [];
        this.rules = ((res && res.rules) || []).map((r) => ({
          ...r,
          overrides: r.overrides || {},
        }));
        // JS-1920 follow-up (2026-11-02): only the client-scope response carries agency_name;
        // on agency scope (no clientId) it's absent and the fallback-cell UI doesn't fire anyway.
        this.agencyName = (res && res.agency_name) || "";
        this.loading = false;
      },
      () => {
        this.loading = false;
      }
    );
  }

  // ---- matrix shape --------------------------------------------------------

  get columns(): Column[] {
    const cols: Column[] = [{ id: null, name: "Default", isDefault: true }];
    this.projectTypes.forEach((pt) =>
      cols.push({ id: pt.id, name: pt.name, isDefault: false })
    );
    return cols;
  }

  /** CSS grid template: rule-label column + Default column + one per project type. */
  get gridTemplate(): string {
    const types = this.projectTypes.length;
    // JS-2014: every value column is fixed at 104px (9rem box + 0.5rem gutter on
    // each side) and the card hugs its content, so Default and the project types
    // sit right after the rule names instead of stretching across the page.
    // `repeat(0, ...)` is invalid CSS (it made the browser drop the whole template
    // and stack every cell in its own row), so it is only emitted with types.
    return "minmax(240px,max-content) 104px" + (types ? ` repeat(${types}, 104px)` : "");
  }

  trackByRule(_i: number, rule: BusinessRule): number {
    return rule.business_rule_id;
  }

  trackByCol(_i: number, col: Column): string {
    return col.id === null ? "default" : String(col.id);
  }

  // ---- value helpers -------------------------------------------------------

  private hasOwn(obj: any, key: any): boolean {
    return obj && Object.prototype.hasOwnProperty.call(obj, String(key));
  }

  isCompound(rule: BusinessRule): boolean {
    return rule.value_type === "compound";
  }

  /** Effective value shown in a cell (override if present, else Default). */
  effVal(rule: BusinessRule, typeId: number | null): any {
    if (typeId === null) {
      return rule.default_value;
    }
    if (this.hasOwn(rule.overrides, typeId)) {
      return rule.overrides[String(typeId)];
    }
    return rule.default_value;
  }

  isOverride(rule: BusinessRule, typeId: number | null): boolean {
    if (typeId === null) {
      return false;
    }
    if (!this.hasOwn(rule.overrides, typeId)) {
      return false;
    }
    return !this.isEmpty(rule.overrides[String(typeId)]);
  }

  private isEmpty(v: any): boolean {
    if (v === null || v === undefined || v === "") {
      return true;
    }
    if (Array.isArray(v)) {
      return v.every((x) => x === null || x === undefined || x === "");
    }
    return false;
  }

  private peakOf(v: any): any {
    return Array.isArray(v) ? v[0] : v;
  }

  private restOf(v: any): any {
    return Array.isArray(v) ? v[1] : null;
  }

  /** Main text rendered in a cell. */
  displayValue(rule: BusinessRule, typeId: number | null): string {
    const peak = this.peakOf(this.effVal(rule, typeId));
    if (peak === null || peak === undefined || peak === "") {
      // JS-1920 follow-up (2026-11-02): fall back to the agency default so the cell shows
      // the actual value (styled grey + tooltipped elsewhere), not an empty-dash.
      const agencyPeak = this.peakOf(rule.agency_default_value);
      if (agencyPeak !== null && agencyPeak !== undefined && agencyPeak !== "") {
        return agencyPeak + (rule.unit || "");
      }
      return "–"; // en dash = not set / not enforced here
    }
    return peak + (rule.unit || "");
  }

  /**
   * JS-1920 follow-up (2026-11-02): true when the cell is rendering the agency's default
   * as a placeholder (no client-level value / override, but the agency has one). Drives the
   * grey "wts__val--agency-fallback" style + "<agency> default" tooltip.
   */
  showingAgencyFallback(rule: BusinessRule, typeId: number | null): boolean {
    const effPeak = this.peakOf(this.effVal(rule, typeId));
    if (effPeak !== null && effPeak !== undefined && effPeak !== "") {
      return false;
    }
    const agencyPeak = this.peakOf(rule.agency_default_value);
    return agencyPeak !== null && agencyPeak !== undefined && agencyPeak !== "";
  }

  /** Tooltip label for the grey placeholder cells. */
  agencyDefaultTooltip(): string {
    const name = this.agencyName && this.agencyName.trim() ? this.agencyName.trim() : "";
    return (name ? name + " " : "") + "default";
  }

  /**
   * A rule is "active" (enforced somewhere) when it has a Default value or at
   * least one project-type override. Rules with no value anywhere are shown
   * muted - visible but doing nothing until a value is entered.
   */
  isRuleActive(rule: BusinessRule): boolean {
    if (!this.isEmpty(rule.default_value)) {
      return true;
    }
    const overrides = rule.overrides || {};
    return Object.keys(overrides).some((k) => !this.isEmpty(overrides[k]));
  }

  /**
   * Visual state of a cell's value: the explicit Default, an explicit override,
   * a project type inheriting a Default value (JS-2014: normal text colour, so it
   * does not look disabled), or muted when there is no value at all.
   */
  valState(rule: BusinessRule, typeId: number | null): "default" | "override" | "inherited" | "muted" {
    if (typeId === null) {
      return this.isEmpty(rule.default_value) ? "muted" : "default";
    }
    if (this.isOverride(rule, typeId)) {
      return "override";
    }
    return this.isEmpty(rule.default_value) ? "muted" : "inherited";
  }

  /** Sub text for compound rules (the "rest" cap). */
  subValue(rule: BusinessRule, typeId: number | null): string {
    if (!this.isCompound(rule)) {
      return "";
    }
    const rest = this.restOf(this.effVal(rule, typeId));
    if (rest === null || rest === undefined || rest === "") {
      return "";
    }
    return "rest ≤ " + rest + (rule.unit || "");
  }

  hasSub(rule: BusinessRule, typeId: number | null): boolean {
    return this.subValue(rule, typeId) !== "";
  }

  // ---- hover state ---------------------------------------------------------

  onCellEnter(ruleId: number, typeId: number | null): void {
    this.hoverRule = ruleId;
    this.hoverType = typeId;
  }

  onCellLeave(): void {
    this.hoverRule = null;
    this.hoverType = null;
  }

  isHoverExact(ruleId: number, typeId: number | null): boolean {
    return this.hoverRule === ruleId && this.hoverType === typeId;
  }

  // ---- inline editing ------------------------------------------------------

  isEditing(ruleId: number, typeId: number | null): boolean {
    return (
      !!this.edit && this.edit.ruleId === ruleId && this.edit.typeId === typeId
    );
  }

  startEdit(rule: BusinessRule, typeId: number | null): void {
    if (this.isEditing(rule.business_rule_id, typeId)) {
      return;
    }
    const value = this.effVal(rule, typeId);
    this.edit = {
      ruleId: rule.business_rule_id,
      typeId,
      compound: this.isCompound(rule),
    };
    this.editVal =
      this.peakOf(value) === null || this.peakOf(value) === undefined
        ? ""
        : String(this.peakOf(value));
    this.editVal2 =
      this.restOf(value) === null || this.restOf(value) === undefined
        ? ""
        : String(this.restOf(value));

    // Focus the freshly-rendered input so the caret shows on the first click.
    setTimeout(() => {
      const input = this.editInputs && this.editInputs.first;
      if (input) {
        input.nativeElement.focus();
      }
    });
  }

  /** Label for a compound rule's sub-input (peak / rest), from the rule config. */
  subLabel(rule: BusinessRule, index: number): string {
    if (rule.sub_labels && rule.sub_labels[index]) {
      return rule.sub_labels[index];
    }
    return index === 0 ? "peak" : "rest";
  }

  cancelEdit(): void {
    this.edit = null;
    this.editVal = "";
    this.editVal2 = "";
  }

  commitEdit(): void {
    const edit = this.edit;
    if (!edit) {
      return;
    }
    const rule = this.rules.find((r) => r.business_rule_id === edit.ruleId);
    const typeId = edit.typeId;
    if (!rule) {
      this.cancelEdit();
      return;
    }

    const toNum = (s: string): number | null => {
      if (s === null || s === undefined || String(s).trim() === "") {
        return null;
      }
      const n = parseFloat(String(s).replace(",", "."));
      return isNaN(n) ? null : n;
    };

    let value: any;
    if (rule.value_type === "compound") {
      value = [toNum(this.editVal), toNum(this.editVal2)];
    } else {
      value = toNum(this.editVal);
    }

    if (typeId === null) {
      rule.default_value = value;
    } else if (this.isEmpty(value)) {
      // clearing an override -> inherit the Default again
      delete rule.overrides[String(typeId)];
    } else {
      rule.overrides[String(typeId)] = value;
    }

    this.cancelEdit();
    this.persist();
  }

  onEditKey(event: KeyboardEvent): void {
    if (event.key === "Enter") {
      event.preventDefault();
      this.commitEdit();
    } else if (event.key === "Escape") {
      this.cancelEdit();
    }
  }

  selectOnFocus(event: any): void {
    if (event && event.target && event.target.select) {
      event.target.select();
    }
  }

  resetCell(rule: BusinessRule, typeId: number | null, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    if (typeId === null) {
      return;
    }
    delete rule.overrides[String(typeId)];
    this.persist();
  }

  // ---- persistence ---------------------------------------------------------

  private persist(): void {
    // Enablement is derived from value presence now, so we no longer send an
    // `enabled` flag; the API keeps the (vestigial) column truthy by default.
    const payload = {
      rules: this.rules.map((r) => ({
        business_rule_id: r.business_rule_id,
        default_value: r.default_value,
        overrides: r.overrides,
      })),
    };

    this.saving = true;
    const request$ = this.clientId
      ? this.agencyService.saveClientBusinessRules(this.clientId, payload)
      : this.agencyService.saveBusinessRules(payload);
    request$.subscribe(
      (res) => {
        this.saving = false;
        if (res && res.rules) {
          this.rules = res.rules.map((r) => ({
            ...r,
            overrides: r.overrides || {},
          }));
        }
        // JS-2014: same toast as the other saves on the agency profile page,
        // shown after an inline edit and after "reset to default".
        this.messageService.add({
          severity: "success",
          summary: this.translate.instant("Success!"),
          detail: this.translate.instant("Data saved successfully!"),
        });
      },
      () => {
        this.saving = false;
        this.messageService.add({
          severity: "error",
          summary: this.translate.instant("error"),
          detail: this.translate.instant("somethingWentWrong"),
        });
      }
    );
  }
}
